import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { getPublicJobs, getPublicJob, applyJob } from "../api";
import { Briefcase, MapPin, Clock, Euro, ArrowLeft, Send, Loader2, CheckCircle2, Upload } from "lucide-react";
import Footer from "../components/Footer";
import Header from "../components/Header";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "");

const TopBar = () => <Header solid />;

const JobMeta = ({ j }) => (
  <p className="text-sm text-neutral-600 flex flex-wrap gap-x-4 gap-y-1">
    <span className="flex items-center gap-1"><Briefcase size={14} className="text-brand" /> {j.type_contrat_label}</span>
    {(j.lieu || j.ville) && <span className="flex items-center gap-1"><MapPin size={14} className="text-brand" /> {j.lieu || j.ville}{j.code_postal ? ` (${j.code_postal})` : ""}</span>}
    {j.horaires && <span className="flex items-center gap-1"><Clock size={14} className="text-brand" /> {j.horaires}</span>}
    {j.salaire && <span className="flex items-center gap-1"><Euro size={14} className="text-brand" /> {j.salaire}</span>}
  </p>
);

const ApplyForm = ({ job }) => {
  const [f, setF] = useState({ nom: "", email: "", telephone: "", message: "" });
  const [cv, setCv] = useState(null);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setState("loading"); setError("");
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      if (cv) fd.append("cv", cv);
      await applyJob(job.id, fd);
      setState("done");
    } catch (err) { setError(err?.response?.data?.detail || "Envoi impossible, réessayez."); setState("idle"); }
  };
  if (state === "done") return <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center" data-testid="apply-success"><CheckCircle2 size={36} className="mx-auto text-emerald-600 mb-2" /><p className="font-heading font-bold text-lg text-neutral-900">Candidature envoyée !</p><p className="text-sm text-neutral-600 mt-1">Merci {f.nom}, nous vous recontactons très vite.</p></div>;
  const cls = "w-full border border-neutral-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-brand";
  return (
    <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-xl p-6 grid sm:grid-cols-2 gap-3" data-testid="apply-form">
      <h3 className="sm:col-span-2 font-heading font-bold text-lg text-neutral-900">Postuler à cette offre</h3>
      <input required placeholder="Nom et prénom *" className={cls} value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} data-testid="apply-nom" />
      <input required type="email" placeholder="Email *" className={cls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="apply-email" />
      <input placeholder="Téléphone" className={cls} value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} data-testid="apply-tel" />
      <label className={`${cls} flex items-center gap-2 cursor-pointer text-neutral-600`}><Upload size={15} /> {cv ? cv.name : "CV (PDF, DOC, DOCX — 5 Mo max)"}<input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setCv(e.target.files[0])} data-testid="apply-cv" /></label>
      <textarea rows={4} placeholder="Quelques mots sur vous, vos disponibilités…" className={`${cls} sm:col-span-2`} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="apply-message" />
      {error && <p className="sm:col-span-2 text-sm text-red-600" data-testid="apply-error">{error}</p>}
      <button type="submit" disabled={state === "loading"} className="sm:col-span-2 bg-brand hover:bg-brand-dark text-white font-semibold py-3 rounded-md flex items-center justify-center gap-2 disabled:opacity-60" data-testid="apply-submit">
        {state === "loading" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Envoyer ma candidature
      </button>
      <p className="sm:col-span-2 text-[11px] text-neutral-400">Vos données sont utilisées uniquement pour le traitement de votre candidature (RGPD).</p>
    </form>
  );
};

const JobDetail = ({ id }) => {
  const [job, setJob] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { getPublicJob(id).then((r) => setJob(r.data)).catch(() => setErr("Cette offre n'est plus disponible.")); }, [id]);
  if (err) return <p className="text-center text-neutral-500 py-16" data-testid="job-unavailable">{err} <Link to="/recrutement" className="text-brand underline">Voir les offres</Link></p>;
  if (!job) return <p className="text-center text-neutral-400 py-16">Chargement…</p>;
  return (
    <div className="max-w-3xl mx-auto px-5 py-10 space-y-6" data-testid="job-detail">
      <Link to="/recrutement" className="text-sm text-brand hover:underline flex items-center gap-1"><ArrowLeft size={14} /> Toutes les offres</Link>
      <div>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-neutral-900">{job.titre}</h1>
        <div className="mt-3"><JobMeta j={job} /></div>
        {job.accroche && <p className="mt-4 text-base text-neutral-700 font-medium">{job.accroche}</p>}
      </div>
      <div className="prose prose-neutral max-w-none bg-white border border-neutral-200 rounded-xl p-6 [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-neutral-900 [&_h3]:mt-4 [&_ul]:list-disc [&_ul]:ml-5 [&_p]:my-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.description_html || "") }} data-testid="job-description" />
      {job.competences?.length > 0 && <div className="flex flex-wrap gap-2">{job.competences.map((c) => <span key={c} className="text-xs bg-brand/10 text-brand font-semibold px-2.5 py-1 rounded-full">{c}</span>)}</div>}
      <ApplyForm job={job} />
    </div>
  );
};

const JobList = () => {
  const [jobs, setJobs] = useState(null);
  useEffect(() => { getPublicJobs().then((r) => setJobs(r.data)).catch(() => setJobs([])); }, []);
  return (
    <div className="max-w-5xl mx-auto px-5 py-12" data-testid="jobs-page">
      <p className="text-brand font-semibold text-sm uppercase tracking-wider">Recrutement</p>
      <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-neutral-900 mt-2">Rejoignez l'équipe PTITSERVICES-PRO</h1>
      <p className="text-base text-neutral-600 mt-4 max-w-2xl">Entreprise de nettoyage B2B des Hauts-de-France, nous recrutons des agents de propreté sérieux et motivés. Matériel fourni, équipe soudée, interventions proches de chez vous.</p>
      <div className="mt-10 grid md:grid-cols-2 gap-5">
        {jobs === null && <p className="text-neutral-400">Chargement des offres…</p>}
        {jobs?.length === 0 && <div className="md:col-span-2 bg-white border border-neutral-200 rounded-xl p-8 text-center text-neutral-500" data-testid="jobs-empty">Aucune offre ouverte pour le moment. Envoyez-nous une candidature spontanée à <a href="mailto:contact@ptitservices-pro.com" className="text-brand underline">contact@ptitservices-pro.com</a>.</div>}
        {jobs?.map((j) => (
          <Link key={j.id} to={`/recrutement/${j.id}`} className="group bg-white border border-neutral-200 rounded-xl p-6 hover:border-brand hover:shadow-md transition-all" data-testid={`job-card-${j.id}`}>
            <span className="text-xs font-semibold text-brand bg-brand/10 px-2 py-0.5 rounded-full">{j.type_contrat_label}</span>
            <h2 className="font-heading font-bold text-lg text-neutral-900 mt-3 group-hover:text-brand">{j.titre}</h2>
            <div className="mt-2"><JobMeta j={j} /></div>
            {j.accroche && <p className="text-sm text-neutral-600 mt-3 line-clamp-2">{j.accroche}</p>}
            <p className="text-xs text-neutral-400 mt-4">Publiée le {fmtDate(j.published_at)} · Voir l'offre →</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default function Recrutement() {
  const { id } = useParams();
  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <TopBar />
      <main className="flex-1">{id ? <JobDetail id={id} /> : <JobList />}</main>
      <Footer />
    </div>
  );
}
